// useCampaignData.js - Campaign數據管理 Composable (精簡優化版)
import { computed, readonly } from 'vue'
import { campaignCache } from '../utils/campaignCache'
import { getUrlLocale } from '../utils/getUrlLocale'
import { campaignDataState, campaignActions, campaignComputed } from '../stores/campaignState'

const CAMPAIGN_API = {
  CAMPAIGN: (id, mode) => `/api/v1/campaigns/${id}${mode === 'preview' ? '?preview=true' : ''}`,
  REWARDS: (id, mode) => `/api/v1/campaigns/${id}/rewards${mode === 'preview' ? '?preview=true' : ''}`,
  NEWS: (id, mode) => `/api/v1/campaigns/${id}/updates${mode === 'preview' ? '?preview=true' : ''}`,
  FAQS: (id, mode) => `/api/v1/campaigns/${id}/faqs${mode === 'preview' ? '?preview=true' : ''}`
}

// Optimized cache time for each data type (milliseconds)
const DEFAULT_TTL = {
  campaign: 15 * 60 * 1000,    // 5分→15分 (基本信息變動少)
  rewards: 20 * 60 * 1000,     // 10分→20分 (獎勵相對穩定)
  news: 5 * 60 * 1000,         // 維持5分 (更新較頻繁)
  faqs: 30 * 60 * 1000         // 10分→30分 (FAQ很少改動)
}

// === Campaign 工具函數 ===
export function useCampaignUtils() {
  // 獲取當前語言設定
  const getCurrentLocale = () => {
    return window.global_values?.LOCALE || 'en'
  }

  // 獲取翻譯函數
  const t = (key, options = {}) => {
    if (window.i18nextGlobal) {
      return window.i18nextGlobal(key, options)
    }
    // Fallback 機制
    return key
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const locale = getCurrentLocale()
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
    return date.toLocaleDateString(locale, options)
  }

  const timeAgo = (date) => {
    if (!date) return ''
    const now = new Date()
    const past = new Date(date)
    const diffInSeconds = Math.floor((now - past) / 1000)

    if (diffInSeconds < 60) {
      return t('campaign.time_ago.seconds', { count: diffInSeconds })
    }
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return t('campaign.time_ago.minutes', { count: minutes })
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return t('campaign.time_ago.hours', { count: hours })
    }
    const days = Math.floor(diffInSeconds / 86400)
    return t('campaign.time_ago.days', { count: days })
  }

  return {
    formatDate,
    timeAgo
  }
}

// === Campaign News 管理器 ===
export function useCampaignNews() {
  const parseNewsHash = (hash) => {
    const parts = hash.split('/')
    if (parts[0] === 'news' && parts.length > 1) {
      const updateId = parseInt(parts[1])
      return {
        mode: 'detail',
        updateId: updateId
      }
    }
    return { mode: 'list' }
  }

  const findUpdateById = (updates, id) => {
    return updates.find(update => update.id === parseInt(id))
  }

  const setNewsDetailMode = (update) => {
    campaignActions.setNewsViewMode('detail')
    campaignActions.setSelectedNewsUpdate(update)
  }

  const setNewsListMode = () => {
    campaignActions.setNewsViewMode('list')
    campaignActions.setSelectedNewsUpdate(null)
  }

  const updateNewsURLHash = (updateId = null) => {
    window.location.hash = updateId ? `news/${updateId}` : 'news'
  }

  return {
    parseNewsHash,
    findUpdateById,
    setNewsDetailMode,
    setNewsListMode,
    updateNewsURLHash,
    // 提供全局狀態的 readonly 存取
    newsViewMode: readonly(computed(() => campaignDataState.newsViewMode)),
    selectedNewsUpdate: readonly(computed(() => campaignDataState.selectedNewsUpdate))
  }
}

// === Campaign 獎勵管理器 ===
export function useCampaignRewards() {
  const updateRewardQuantity = (rewardId, quantity) => {
    campaignActions.updateRewardQuantity(rewardId, quantity)
  }

  const clearRewardQuantities = () => {
    campaignActions.clearRewardQuantities()
  }

  return {
    updateRewardQuantity,
    clearRewardQuantities,
    // 提供全局狀態的 readonly 存取
    rewardQuantities: readonly(computed(() => campaignDataState.rewardQuantities)),
    totalQuantity: readonly(campaignComputed.totalQuantity)
  }
}


export function useCampaignData(campaignId, campaignMode = 'show') {
  const id = String(campaignId)
  const mode = campaignMode
  const locale = getUrlLocale()
  campaignActions.setCampaignId(id)

  const fetchWithCache = async (dataType, apiUrl, customTTL = null) => {
    if (mode === 'preview') {
      return performAPIRequest(dataType, apiUrl, customTTL)
    }

    const cacheKey = `${id}_${dataType}_${locale}`

    if (campaignActions.hasData(dataType)) {
      return campaignActions.getData(dataType)
    }

    const cached = campaignCache.get(cacheKey)
    if (cached) {
      campaignActions.setData(dataType, cached)
      return cached
    }

    return performAPIRequest(dataType, apiUrl, customTTL)
  }

  const performAPIRequest = async (dataType, apiUrl, customTTL) => {
    const cacheKey = `${id}_${dataType}_${locale}`

    try {
      campaignActions.setLoading(dataType, true)

      const response = await fetch(apiUrl, {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        // Preview 模式的容錯處理
        if (mode === 'preview' && response.status === 404) {
          const emptyData = getEmptyDataForType(dataType)
          campaignActions.setData(dataType, emptyData)
          return emptyData
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      // 處理不同API的響應格式
      let processedData = data
      if (dataType === 'news' && data.data) {
        // News 從專用 /updates API 獲取，已經格式化好的創建者評論
        processedData = data.data
      }

      campaignActions.setData(dataType, processedData)
      if (mode !== 'preview') {
        campaignCache.set(cacheKey, processedData, customTTL)
      }

      return processedData

    } catch (error) {
      campaignActions.setError(dataType, error.message)
      throw error
    } finally {
      campaignActions.setLoading(dataType, false)
    }
  }

  const createFetcher = (dataType) => async (forceRefresh = false) => {
    if (forceRefresh) {
      campaignActions.clearDataType(dataType)
      campaignCache.remove(`${id}_${dataType}_${locale}`)
    }
    return fetchWithCache(dataType, CAMPAIGN_API[dataType.toUpperCase()](id, mode), DEFAULT_TTL[dataType])
  }

  // Provides empty data structures
  const getEmptyDataForType = (dataType) => {
    switch (dataType) {
      case 'campaign':
        return null
      case 'rewards':
      case 'news':
      case 'faqs':
        return []
      default:
        return null
    }
  }

  const fetchCampaignData = createFetcher('campaign')
  const fetchRewards = createFetcher('rewards')
  const fetchNews = createFetcher('news')
  const fetchFaqs = createFetcher('faqs')

  // 整合新的 composables
  const newsManager = useCampaignNews()
  const rewardsManager = useCampaignRewards()
  const utils = useCampaignUtils()

  return {
    // === 原有數據獲取功能 ===
    fetchCampaignData,
    fetchRewards,
    fetchNews,
    fetchFaqs,

    // === 原有數據狀態 ===
    campaignData: readonly(computed(() => campaignDataState.campaignData)),
    rewardsData: readonly(computed(() => campaignDataState.rewardsData)),
    newsUpdates: readonly(computed(() => campaignDataState.newsUpdates)),
    faqsData: readonly(computed(() => campaignDataState.faqsData)),

    // === 原有載入狀態 ===
    isBasicDataReady: campaignComputed.isBasicDataReady,
    isAnyLoading: campaignComputed.isAnyLoading,
    hasErrors: campaignComputed.hasErrors,
    allErrors: campaignComputed.allErrors,
    loadingTypes: campaignComputed.loadingTypes,

    isLoading: (dataType) => computed(() => campaignDataState.loading[dataType] || false),
    getError: (dataType) => computed(() => campaignDataState.errors[dataType]),

    // === News 管理功能 ===
    ...newsManager,

    // === 獎勵管理功能 ===
    ...rewardsManager,

    // === 工具函數 ===
    utils,

    // === 其他 ===
    clearCache: () => campaignCache.clear(),
    campaignId: id
  }
}
